/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { ConnectionManager } from './ConnectionManager';
import {
  INSTANT_KPI_CHANNEL,
  instantKpiEvent,
  instantKpiOf,
  limitWindowKeys,
  METER_LATEST_EVENT,
  METER_READING_EVENT,
  WINDOW_METRICS_CHANNEL,
  WINDOW_METRICS_INCOMERS_EVENT,
  WINDOW_METRICS_METER_EVENT,
  windowKeyOf,
} from './protocol';
import { SubscriptionHub } from './SubscriptionHub';
import { INSTANT_KPIS } from '../features/meters/instantKpis';
import {
  checkSession,
  closeStreamConnection,
  instantKpiStreamUrl,
  requestStreamTicket,
  windowMetricsStreamUrl,
} from './ticketClient';

function createManager(eventTypes: string[], buildUrl?: (streamUrl: string, keys: string[]) => string, retryCapMs?: number): ConnectionManager {
  return new ConnectionManager({
    requestTicket: (forceRefresh) => requestStreamTicket(forceRefresh),
    checkSession,
    createEventSource: (url) => new EventSource(url),
    setTimeout: (callback, ms) => setTimeout(callback, ms),
    clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
    random: Math.random,
    eventTypes,
    buildUrl,
    retryCapMs,
    closeStream: (streamId) => closeStreamConnection(streamId),
  });
}

/** The meter-readings stream plus the instant KPI and window-metrics channels, each with its own ticket and connection. */
export function createBrowserHub(): { hub: SubscriptionHub; managers: ConnectionManager[] } {
  const manager = createManager([METER_READING_EVENT, METER_LATEST_EVENT]);
  const kpiManager = createManager(INSTANT_KPIS.map(instantKpiEvent), instantKpiStreamUrl);
  const windowManager = createManager([WINDOW_METRICS_METER_EVENT, WINDOW_METRICS_INCOMERS_EVENT], windowMetricsStreamUrl, 4_000);
  const hub = new SubscriptionHub(
    manager,
    { setInterval: (callback, ms) => setInterval(callback, ms), now: () => Date.now() },
    {
      [INSTANT_KPI_CHANNEL]: { manager: kpiManager, keyOf: instantKpiOf },
      [WINDOW_METRICS_CHANNEL]: { manager: windowManager, keyOf: windowKeyOf, limitKeys: limitWindowKeys },
    },
  );
  return { hub, managers: [manager, kpiManager, windowManager] };
}
